/** The shapes the robot publishes. Mirrors pick_place.teleop.pick_loop's status and state keys. */

export type ControlMode = "crane" | "pose";
export type Hand = "left" | "right";

export type Settings = {
  gripper_open: number;      // 0.2 .. 1 of the jaws' travel
  speed: number;             // m/s at full stick
  grip_close_speed: number;  // fractions of the travel per second; 0 = shut at once
  mode: ControlMode;
  hand: Hand;
  /** Whether the arm paints its depth cues into the picture: the magenta tint over the
   *  highest points, the ring where the fingertips will land, the green frame when they are
   *  low enough. Off still measures all of it, and still buzzes. */
  overlay: boolean;
  /** What the episodes are recorded as: the instruction a policy is trained on, so it names what
   *  is in the box. Absent on an arm that does not record (or predates the setting). */
  task?: string | null;
};

export type PathState = {
  start: boolean;
  waypoints: number;
  end: boolean;
  /** Where the marks are, in the order the trip drives them (start, the waypoints, end): the tool
   *  point, mm, the arm's base frame. The first is the start when `start` is set, the last the end
   *  when `end` is. Older arms send none. */
  points?: [number, number, number][];
};

export type Driver = {
  operator_id: string;
  display_name: string | null;
  since: number;
  lease_ok: boolean;
} | null;

export type Highlight = {
  top_below_fingers_mm: number | null;
  low: boolean;
  span_mm?: number | null;
} | null;

/** The camera mount against the box the wrist camera has mapped (pick_place.teleop.pick_loop.rim_guard).
 *  `which` is the path that comes closest: where the mount is now, the lift a "picked" grip or A makes,
 *  or -- gripper open over the pile -- the way down to the pile and back up. */
export type Rim = {
  level: "clear" | "near" | "hit";
  clear_mm: number | null;
  which: "now" | "lift" | "down" | null;
  say: string;
  now_mm: number | null;
  lift_mm: number | null;
  down_mm: number | null;
  /** The turn that clears it, about the gripper's own axis: 180 keeps the pads across the same strand. */
  turn_deg: number | null;
  turn_key: string | null;
  turn_full: boolean | null;
  turn_clear_mm: number | null;
  /** The mount point that came closest, mm, the arm's base frame. */
  contact?: [number, number, number] | null;
  /** The tool's way along the path that came closest, mm, base frame (at most ~31 points). */
  path?: [number, number, number][];
  voxels: number;
  frames: number;
  ms?: number;
} | null;

export type Status = {
  type: "status";
  state: string;                 // idle | homing | orient | teach | pick | to_confirm | confirm | trip | to_start | reset | blocked
  confirm_left_s?: number | null; // confirm: seconds left for the second "picked" grip
  mode: string;
  crane: boolean;
  message: string;
  cycle: number;
  trips: number;
  gripper_closed: boolean;
  gripper_open: number;
  gripper_open_mm: number;
  settings?: Settings;
  driver?: Driver;
  /** The name this arm takes controller frames and commands under. Publish anywhere else and
   *  it hears nothing, with no error to say so. */
  operator?: string;
  taught: PathState;
  next_mark: string | null;
  trip: string[];
  trip_index: number;
  paused: boolean;
  segment: string | null;
  action_id: string | null;
  highlight: Highlight;
  /** Older arms, and an arm without a depth camera, send none. */
  rim?: Rim;
  input_age: number | null;
  episodes: number;
  recording: boolean;
  task: string | null;
  yaw_deg: number;
  /** The tool point, measured: metres, the arm's base frame. */
  ee?: [number, number, number];
  /** ... and which way the tool points, as a quaternion (x, y, z, w). Older arms send none. */
  ee_quat?: [number, number, number, number];
  /** Where the gripper end's bodies are (measured): {body: [x, y, z, qx, qy, qz, qw]}, base frame. With the box map. */
  tool_poses?: Record<string, number[]> | null;
  /** How the link to whoever is driving is doing. Older arms send none. */
  perf?: Perf;
};

/** Percentiles of a latency over the arm's last ten seconds, in ms. */
export type Spread = { p50: number; p95?: number; p99?: number; max: number } | null;

export type PerfEventKind = "input_gap" | "input_lost" | "video_freeze" | "headset_hitch" | "loop_late" | "camera_gap";

export type PerfEvent = { kind: PerfEventKind; ms: number; ago_s: number; source?: string; camera?: string };

export type EpisodePerf = {
  duration_s: number;
  controls_ms: Spread;
  video_ms: Spread;
  rtt_ms: Spread;
  felt_ms?: number | null;
  stutters: number;
  stutter_counts: Partial<Record<PerfEventKind, number>>;
  controller_frames_lost: number;
};

/** Mirrors pick_place.teleop.link_perf.LinkPerf.snapshot(). */
export type Perf = {
  /** Controller frames, page to arm, timed on the relay's shared clock as they land. */
  controls: { from: string; hz: number; ms: Spread; age_ms: number; lost_pct: number; max_gap_ms: number | null } | null;
  /** What the driving page reported of the video it showed: camera to headset. */
  video: { from: string | null; track: string | null; fps: number | null; ms: Spread; max_gap_ms: number | null;
           dropped: number } | null;
  headset: { fps: number | null; dropped: number } | null;
  rtt_ms: Spread;
  /** Controls plus video: a stick moved to that movement seen, less the arm's own lag. */
  felt_ms: number | null;
  loop: { hz: number; p99_ms: number; busy_p99_ms: number | null } | null;
  cameras: Record<string, { fps: number | null; skips_1m: number }>;
  clock: { arm: boolean; page: boolean | null; page_error_ms: number | null };
  stutters_1m: number;
  events: PerfEvent[];
  episode: { s: number; stutters: number } | null;
  last_episode: EpisodePerf | null;
  /** The newest numbered controller frame and how long the arm held it before this status went out. */
  echo: { source: string; side: "left" | "right" | null; seq: number; held_ms: number } | null;
};

/** One controller, exactly as pick_place.teleop.adamo.operator documents the topic. */
export type HandFrame = {
  stamp: number;
  operator_id: string;
  /** This hand's frame counter: the arm counts the ones that never arrived. */
  seq?: number;
  /** The relay's shared clock when the frame left, in µs; null until this page has synced it. */
  sent_us?: number | null;
  pos: [number, number, number] | null;
  quat: [number, number, number, number] | null;
  head_quat: [number, number, number, number] | null;
  emulated: boolean;
  trigger: number;
  squeeze: boolean;
  stick_press: boolean;
  a: boolean;
  b: boolean;
  stick: [number, number];
};

export type Haptic = { type: "haptic"; hand: Hand; intensity: number; ms: number };

export const SETTING_LIMITS = {
  gripper_open: { min: 0.2, max: 1, step: 0.05 },
  speed: { min: 0.01, max: 0.3, step: 0.01 },
  grip_close_speed: { min: 0, max: 8, step: 0.5 },
} as const;
